import apiClient from '../../../../utils/api'
import { NO_CAPACITY_LABEL, NO_TIMEFRAME_LABEL } from '../shared/propertyOptions'
import { TGoalDraft, TScenarioPath } from '../shared/scenario'
import { startingPointForDraft } from '../shared/startingPoint'

export type TScaffoldDraftInput = Pick<
    TGoalDraft,
    'title' | 'why' | 'timeframeLabel' | 'capacityLabel' | 'startingPoint'
>

/**
 * A revision note and the plan it is about. The endpoint stores no drafts, so the plan
 * being critiqued travels with the note — and it is the plan as edited on screen, not
 * the one the planner last returned.
 */
export interface TGoalDraftRevision {
    feedback: string
    currentPlan: TScenarioPath
}

const plannerLabel = (value: string, unsetLabel: string) => (value === unsetLabel ? '' : value)

export type TGoalDraftErrorCode =
    | 'no_key'
    | 'subscription'
    | 'model'
    | 'timeout'
    | 'empty_plan'
    | 'unchanged_plan'
    | 'network'
    | 'canceled'

export interface TGoalDraftError {
    code: TGoalDraftErrorCode
    message: string
    detail?: string
}

export type TGoalDraftResult =
    | { status: 'ready'; paths: TScenarioPath[]; error?: undefined }
    | { status: 'error'; paths: []; error: TGoalDraftError }

export const GOAL_DRAFT_UNCHANGED_MESSAGE = 'Plan did not change. Check your note and capacity.'

const compactPlanText = (value?: string) => value?.trim().replace(/\s+/g, ' ') ?? ''

/**
 * Compare only fields Review and Accept can make real. Regenerated ids and rewritten
 * rationale do not satisfy feedback when the type, phases, and included plan rows are
 * otherwise identical. The endpoint also rejects a normalized no-op; this remains a
 * compatibility guard for older backends and local adapters so neither review surface can
 * report a no-op as applied.
 */
export const goalDraftPlanMateriallyUnchanged = (current: TScenarioPath, returned: TScenarioPath): boolean => {
    const materialPlan = (path: TScenarioPath) => ({
        type: path.type,
        phases: (path.phases ?? []).map((phase) => ({
            name: compactPlanText(phase.name),
            cadenceLabel: compactPlanText(phase.cadenceLabel),
            weeklyHours: phase.weeklyHours,
            dateSpanLabel: compactPlanText(phase.dateSpanLabel),
            weeks: phase.weeks,
        })),
        items: path.items
            .filter((item) => item.included)
            .map((item) => ({
                kind: item.kind,
                title: compactPlanText(item.title),
                frequencyLabel: compactPlanText(item.frequencyLabel),
            })),
    })
    return JSON.stringify(materialPlan(current)) === JSON.stringify(materialPlan(returned))
}

const draftErrorFromUnknown = (err: unknown): TGoalDraftError => {
    const maybeError = err as {
        code?: string
        message?: string
        response?: { status?: number; data?: { detail?: string; error?: TGoalDraftError } }
    }
    const detail = maybeError.response?.data?.detail
    if (maybeError.code === 'ERR_CANCELED') return { code: 'canceled', message: 'Canceled' }
    if (maybeError.response?.status === 403) {
        return { code: 'subscription', message: 'Subscription needed', detail }
    }
    return { code: 'network', message: 'Network error', detail: maybeError.message }
}

export const draftGoalPaths = async (
    draft: TScaffoldDraftInput,
    signal?: AbortSignal,
    revision?: TGoalDraftRevision
): Promise<TGoalDraftResult> => {
    if (draft.title.trim().length === 0) {
        return { status: 'error', paths: [], error: { code: 'empty_plan', message: 'No plan returned' } }
    }
    const payload = {
        title: draft.title.trim(),
        why: draft.why.trim(),
        timeframeLabel: plannerLabel(draft.timeframeLabel, NO_TIMEFRAME_LABEL),
        capacityLabel: plannerLabel(draft.capacityLabel, NO_CAPACITY_LABEL),
        ...(draft.startingPoint ? { startingPoint: startingPointForDraft(draft.startingPoint) } : {}),
        ...(revision && revision.feedback.trim().length > 0
            ? { feedback: revision.feedback.trim(), currentPlan: revision.currentPlan }
            : {}),
    }
    try {
        const res = signal
            ? await apiClient.post('/goals/draft_paths/', payload, { signal })
            : await apiClient.post('/goals/draft_paths/', payload)
        const result = res.data as TGoalDraftResult
        if (result.status === 'ready' && result.paths.length > 0) {
            if (revision && goalDraftPlanMateriallyUnchanged(revision.currentPlan, result.paths[0])) {
                return {
                    status: 'error',
                    paths: [],
                    error: { code: 'unchanged_plan', message: 'Plan was not revised' },
                }
            }
            return result
        }
        return {
            status: 'error',
            paths: [],
            error: result.error ?? { code: 'empty_plan', message: 'No plan returned' },
        }
    } catch (err) {
        return { status: 'error', paths: [], error: draftErrorFromUnknown(err) }
    }
}

export const buildScaffoldReviewDraft = (draftInput: TScaffoldDraftInput, draftPaths: TScenarioPath[]): TGoalDraft => {
    const draftedPath = draftPaths[0]
    return {
        ...draftInput,
        items: draftedPath?.items ?? [],
        phases: draftedPath?.phases,
        goalType: draftedPath?.type,
    }
}
